"use client";

import { ReactNode, Suspense } from "react";
import { Toaster } from "react-hot-toast";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "@npm_leadtech/qr-ui-components";

import { StoreUtms } from "@/components/StoreUtms/StoreUtms";
import { HistoryEvent } from "@/components/HistoryEvent";

import { LayoutProvider } from "@/contexts/LayoutContext";
import { UserProvider } from "@/contexts/UserContext";
import { ModalProvider } from "@/contexts/ModalContext";
import { GlobalProvider } from "@/contexts/GlobalContext/GlobalContext";
import { LoadingBarProvider } from "@/contexts/LoadingBarContext/LoadingBarContext";
import { QREditorProvider } from "@/contexts/QREditorContext";
import { AmplitudeContextProvider } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { DatalayerProvider } from "@/contexts/DataLayerEventContext";
import { ABTestingProvider } from "@/contexts/ABTestingContext/ABTestingContext";
import { ThirdPartyScripts } from "./ThirdPartyScripts";

import { usePrefetchRouter } from "@/hooks/navigation/usePrefetchRouter";
import { TranslationsProvider } from "@/hocs/TranslationsProvider";
import { getSiteDomain } from "@/utils/site";
import { getGTMId } from "@/utils/gtm";
import { THEME } from "@/themes/types";
// eslint-disable-next-line import/no-unresolved
import { fonts } from "../../public/fonts/fonts";

const currentTheme: THEME = process.env.NEXT_PUBLIC_THEME as THEME;

export const LayoutWrapper = ({
  children,
  lang,
  genericErrors,
  modalData,
  resources,
}: {
  children: ReactNode;
  lang?: string;
  genericErrors: any;
  modalData?: any;
  resources?: any;
}) => {
  usePrefetchRouter([]);

  const fontClasses = Object.values(fonts)
    .map((font: any) => font.variable)
    .join(" ");

  const siteDomain = getSiteDomain();
  const gtmId = getGTMId(siteDomain);

  return (
    <html
      lang={lang || "en-US"}
      className={fontClasses}
    >
      <head>
        <link
          rel="preconnect"
          href="https://dev.visualwebsiteoptimizer.com" // VWO
        />
      </head>
      <HistoryEvent />
      <body>
        <ThirdPartyScripts gtmId={gtmId} />
        <ABTestingProvider>
          <TranslationsProvider
            locale={lang || "en-US"}
            resources={resources}
            namespaces={["generic-errors"]}
          >
            <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ""}>
              <GlobalProvider>
                <AmplitudeContextProvider>
                  <ThemeProvider theme={currentTheme}>
                    <LayoutProvider
                      modalData={modalData}
                      genericErrors={genericErrors}
                    >
                      <UserProvider>
                        <DatalayerProvider>
                          <QREditorProvider>
                            <LoadingBarProvider>
                              <ModalProvider>
                                <Suspense>
                                  <StoreUtms />
                                </Suspense>
                                {children}
                              </ModalProvider>
                            </LoadingBarProvider>
                          </QREditorProvider>
                        </DatalayerProvider>
                      </UserProvider>
                    </LayoutProvider>
                  </ThemeProvider>
                </AmplitudeContextProvider>
              </GlobalProvider>
            </GoogleOAuthProvider>
            <Toaster />
          </TranslationsProvider>
        </ABTestingProvider>
      </body>
    </html>
  );
};
